import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { ProfileContext } from "../../../context/ProfileProvider";
import "../PasskeyPage/passkey.css";
import "./antiPhishing.css";

const METHOD_PRIORITY = ["totp", "email", "mobile"]; // passkey excluded on this page

const AntiPhishing = () => {
  const navigate = useNavigate();
  const profileCtx = useContext(ProfileContext) || {};
  const userDetails = profileCtx.userDetails || {};

  const [statusLoading, setStatusLoading] = useState(true);
  const [hasAntiPhishingCode, setHasAntiPhishingCode] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [availableMethods, setAvailableMethods] = useState({ totp: false, email: false, mobile: false });

  const [antiCode, setAntiCode] = useState("");
  /** "totp" | "email" | "mobile" (passkey excluded) */
  const [verifyMethod, setVerifyMethod] = useState("email");
  const [verifyCode, setVerifyCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const hasAnyVerificationMethod = useMemo(
    () => Boolean(availableMethods?.totp || availableMethods?.email || availableMethods?.mobile),
    [availableMethods]
  );

  const maskedEmail = useMemo(() => {
    const email = String(userDetails?.emailId || userDetails?.email || "").trim();
    if (!email || !email.includes("@")) return "your email";
    const [name, domain] = email.split("@");
    return `${name.slice(0, 2)}***@${domain}`;
  }, [userDetails]);

  const antiCodeClean = useMemo(() => antiCode.replace(/\D/g, "").slice(0, 8), [antiCode]);
  const verifyCodeClean = useMemo(() => verifyCode.replace(/\D/g, "").slice(0, 6), [verifyCode]);

  const isAntiCodeValid = antiCodeClean.length >= 5 && antiCodeClean.length <= 8;
  const needsOtp = verifyMethod === "email" || verifyMethod === "mobile";
  const needsTotpCode = verifyMethod === "totp";
  const isVerifyCodeValid = verifyCodeClean.length === 6;
  const canSubmit = isAntiCodeValid && isVerifyCodeValid && !submitting && hasAnyVerificationMethod && !methodsLoading;

  const fetchStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.antiPhishingGetStatus();
      if (!result?.success) {
        alertErrorMessage(result?.message || "Failed to load anti-phishing status");
        return;
      }
      const data = result?.data || {};
      setHasAntiPhishingCode(!!data?.hasAntiPhishingCode);
      setStatusMessage(typeof data?.message === "string" ? data.message : "");
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setStatusLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  }, []);

  const fetchSecurityMethods = useCallback(async () => {
    setMethodsLoading(true);
    try {
      const result = await AuthService.getSecurityMethodsList();
      const m =
        result?.data?.security_methods ||
        result?.data?.data?.security_methods ||
        result?.security_methods ||
        result?.data?.securityMethods ||
        null;

      if (!result?.success || !m || typeof m !== "object") {
        alertErrorMessage(result?.message || "Could not load verification methods.");
        setAvailableMethods({ totp: false, email: false, mobile: false });
        return;
      }

      const methodsObj = {
        totp: !!m.totp,
        email: !!m.email,
        mobile: !!(m.mobile ?? m.phone ?? m.sms),
      };
      setAvailableMethods(methodsObj);

      const first = METHOD_PRIORITY.find((k) => methodsObj[k]);
      if (first) setVerifyMethod(first);
      else alertErrorMessage("No verification method available. Enable email, mobile, or authenticator in Security first.");
    } catch (e) {
      alertErrorMessage(e?.message || "Could not load verification methods.");
      setAvailableMethods({ totp: false, email: false, mobile: false });
    } finally {
      setMethodsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchStatus();
    void fetchSecurityMethods();
  }, [fetchStatus, fetchSecurityMethods]);

  useEffect(() => {
    setVerifyCode("");
  }, [verifyMethod]);

  const handleSendOtp = async () => {
    if (!needsOtp) return;
    try {
      setSubmitting(true);
      LoaderHelper.loaderStatus(true);
      const target = verifyMethod === "mobile" ? "mobile" : "email";
      const result = await AuthService.antiPhishingSendOtp(target);
      if (result?.success) {
        alertSuccessMessage(result?.message || "OTP sent successfully");
      } else {
        alertErrorMessage(result?.message || "Failed to send OTP");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleAddCode = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      LoaderHelper.loaderStatus(true);
      const params = {
        antiPhishingCode: antiCodeClean,
        verifyMethod,
      };
      params.code = verifyCodeClean;
      const result = await AuthService.antiPhishingAdd(params);
      if (!result?.success) {
        alertErrorMessage(result?.message || "Failed to add anti-phishing code");
        return;
      }
      alertSuccessMessage(result?.message || "Anti-phishing code added successfully");
      setVerifyCode("");
      navigate("/user_profile/security", { replace: true });
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  return (
    <main className="apc-page" aria-labelledby="apc-title">
      <nav className="apc-page__crumbs" aria-label="Breadcrumb">
        <ol className="apc-page__crumbList">
          <li className="apc-page__crumbItem">
            <button type="button" className="apc-page__crumbLink" onClick={() => navigate("/user_profile/security")}>
              Security
            </button>
          </li>
          <li className="apc-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="apc-page__crumbItem apc-page__crumbItem--active" aria-current="page">
            Anti-Phishing Code Settings
          </li>
        </ol>
      </nav>

      <section className="apc-card" aria-label="Anti-Phishing Code form">
        <h1 id="apc-title" className="apc-page__title">
          Anti-Phishing Code Settings
        </h1>

        {statusLoading ? (
          <p className="apc-page__subtitle">Loading…</p>
        ) : (
          <>
            {statusMessage ? <p className="apc-note">{statusMessage}</p> : null}
          </>
        )}

        {!statusLoading && !methodsLoading && !hasAnyVerificationMethod ? (
          <p className="apc-hint">No verification methods are available. Complete security setup first.</p>
        ) : null}

        <div className="apc-field">
          <label className="apc-label" htmlFor="apc-code">
            Anti-Phishing Code
          </label>
          <input
            id="apc-code"
            className="apc-input"
            placeholder="5–8 digits"
            value={antiCodeClean}
            onChange={(e) => setAntiCode(e.target.value)}
            inputMode="numeric"
            disabled={statusLoading || methodsLoading || !hasAnyVerificationMethod}
          />
          <div className="apc-hint">Enter 5 to 8 digits only.</div>
        </div>

        <div className="apc-field">
          <label className="apc-label" htmlFor="apc-verify">
            {needsTotpCode ? "Google Authenticator code" : needsOtp ? "Verification code" : "Code"}
          </label>
          <div className="apc-inputRow">
            <input
              id="apc-verify"
              className="apc-input apc-input--row"
              placeholder="6-digit code"
              value={verifyCodeClean}
              onChange={(e) => setVerifyCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              disabled={statusLoading || methodsLoading || !hasAnyVerificationMethod}
            />
            {needsOtp ? (
              <button type="button" className="apc-send" onClick={handleSendOtp} disabled={submitting || statusLoading}>
                Send
              </button>
            ) : null}
          </div>
          {needsOtp ? (
            <div className="apc-note">
              Send the code to {verifyMethod === "mobile" ? "your mobile" : maskedEmail}. Valid for about 10 minutes.
            </div>
          ) : null}
        </div>

        <button
          type="button"
          className={`apc-primary ${canSubmit ? "is-enabled" : ""}`}
          disabled={!canSubmit || statusLoading}
          onClick={handleAddCode}
        >
          Confirm
        </button>
      </section>
    </main>
  );
};

export default AntiPhishing;
